/**
 * Attendance Trend Chart Component
 * 出席率トレンドをエリアチャートで表示（ダークモード対応）
 */

import { Card, Text, Modal, Stack, Group, Badge, useMantineColorScheme } from '@mantine/core';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CategoricalChartState } from 'recharts/types/chart/types';
import { motion } from 'framer-motion';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { getGlassStyle } from '../styles/darkModeGlass';
import type { AttendanceTrendData, AttendanceTrendMonthly } from '../types/enhanced';

const MotionCard = motion(Card);

export const AttendanceTrendChart = ({ data }: { data: AttendanceTrendData }) => {
  const { colorScheme } = useMantineColorScheme();
  const [modalOpened, { open, close }] = useDisclosure(false);
  const [selectedMonth, setSelectedMonth] = useState<AttendanceTrendMonthly | null>(null);

  const handleAreaClick = (entry: AttendanceTrendMonthly) => {
    setSelectedMonth(entry);
    open();
  };

  return (
    <>
      <MotionCard
        padding="sm"
        radius="sm"
        shadow="sm"
        style={{
          ...getGlassStyle(colorScheme),
          minHeight: '300px',
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Text size="sm" fw={600} mb="xs">
          出席トレンド（クリックで詳細）
        </Text>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart
            data={data.monthly}
            margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
            onClick={(e: CategoricalChartState) => {
              if (e?.activePayload?.[0]) {
                handleAreaClick(e.activePayload[0].payload as AttendanceTrendMonthly);
              }
            }}
          >
            <defs>
              <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4c6ef5" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#4c6ef5" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#666" />
            <YAxis tick={{ fontSize: 11 }} stroke="#666" />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Area
              type="monotone"
              dataKey="rate"
              stroke="#4c6ef5"
              strokeWidth={2}
              fill="url(#colorAttendance)"
              cursor="pointer"
            />
          </AreaChart>
        </ResponsiveContainer>
      </MotionCard>

      {/* 詳細モーダル */}
      <Modal
        opened={modalOpened}
        onClose={close}
        title={`${selectedMonth?.month || ''} 詳細`}
        styles={{
          content: getGlassStyle(colorScheme),
          header: {
            background:
              colorScheme === 'dark' ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(16px)',
          },
          body: {
            background: 'transparent',
          },
        }}
        overlayProps={{
          opacity: 0.5,
          blur: 16,
        }}
      >
        {selectedMonth && (
          <Stack>
            <Group justify="space-between">
              <Text>出席率</Text>
              <Badge size="lg" color="blue">
                {selectedMonth.rate}%
              </Badge>
            </Group>
            <Card
              p="md"
              radius="sm"
              withBorder
              style={{
                background:
                  colorScheme === 'dark' ? 'rgba(50, 50, 50, 0.5)' : 'rgba(255, 255, 255, 0.8)',
                borderColor:
                  colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              }}
            >
              <Text size="sm" c="dimmed">
                この月の出席率は {selectedMonth.rate}% でした。
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                ※ より詳細なデータは今後のアップデートで追加予定です
              </Text>
            </Card>
          </Stack>
        )}
      </Modal>
    </>
  );
};
